import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap, filter, take, map, tap } from 'rxjs';
import { RequestData } from 'src/app/common/models/request-data.model';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpEventType } from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AdsSettingsService {
  private readonly CHUNK_SIZE = 2 * 1024 * 1024; // 5MB chunks

  constructor(private http: HttpClient) { }

  getShopList(requestData: RequestData): Observable<any> {
    return this.http.post(environment.baseUrl + '/common/shop/get', requestData);
  }

  // Component explicitly handles uploading files first, then calls this method with the final filenames
  setAds(payload: any): Observable<any> {
    return this.http.post(environment.baseUrl + '/hq/ads-settings/create-ad', payload);
  }

  getList(requestData: RequestData): Observable<any> {
    return this.http.post(environment.baseUrl + '/hq/ads-settings/get-list', requestData);
  }

  getAdById(id: number): Observable<any> {
    return this.http.get(environment.baseUrl + '/hq/ads-settings/get-ad/' + id);
  }

  updateAd(id: number, payload: any): Observable<any> {
    return this.http.post(environment.baseUrl + '/hq/ads-settings/update-ad/' + id, payload);
  }

  deleteMedia(adId: number, fileName: string): Observable<any> {
    return this.http.post(environment.baseUrl + '/hq/ads-settings/delete-media', { adId, fileName });
  }

  deleteAd(id: number): Observable<any> {
    return this.http.post(environment.baseUrl + '/hq/ads-settings/delete-ad/' + id, {});
  }

  uploadFileChunked(
    file: File,
    fileId: string,
    metadata: Record<string, any> = {},
    onProgress?: (progress: number) => void
  ): Observable<any> {
    const totalChunks = Math.ceil(file.size / this.CHUNK_SIZE);

    const uploadChunk = (chunkIndex: number): Observable<any> => {
      const start = chunkIndex * this.CHUNK_SIZE;
      const end = Math.min(start + this.CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('fileId', fileId);
      formData.append('chunk', chunk, file.name);
      formData.append('chunkIndex', chunkIndex.toString());
      formData.append('totalChunks', totalChunks.toString());
      formData.append('fileName', file.name);
      formData.append('fileSize', file.size.toString());
      formData.append('fileType', file.type);

      Object.keys(metadata).forEach(key => {
        const value = metadata[key];
        if (Array.isArray(value)) {
          value.forEach((item: any) => formData.append(`${key}[]`, String(item)));
        } else if (value !== null && value !== undefined) {
          formData.append(key, String(value));
        }
      });

      const url = environment.baseUrl + '/hq/ads-settings/upload-chunk';

      return this.http.post(url, formData, {
        reportProgress: true,
        observe: 'events'
      }).pipe(
        tap((event: any) => {
          if (event.type === HttpEventType.UploadProgress && onProgress) {
            const uploadedSoFar = (chunkIndex * this.CHUNK_SIZE) + (event.loaded || 0);
            const percent = Math.round((uploadedSoFar / file.size) * 100);
            onProgress(Math.min(percent, 100));
          }
        }),
        filter(event => event.type === HttpEventType.Response),
        take(1),
        map((event: any) => event.body)
      );
    };

    // return last chunk API response
    return this.executeChunksSequentially(0, totalChunks, uploadChunk, null);
  }

  private executeChunksSequentially(
    index: number,
    total: number,
    uploadFn: (index: number) => Observable<any>,
    lastResponse: any = null
  ): Observable<any> {
    if (index >= total) {
      return of(lastResponse ?? { success: true, message: 'All chunks uploaded' });
    }

    return uploadFn(index).pipe(
      switchMap((response) =>
        this.executeChunksSequentially(index + 1, total, uploadFn, response)
      )
    );
  }
}


